import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { SUPPORTED_LOCALES, SupportedLocalesCode } from '.';
import { useGetAvailableLanguages } from '../business/useGetAvailableLanguages';

interface ContextValue {
  activeLanguage: SupportedLocalesCode;
  switchLanguage: (newLang: SupportedLocalesCode) => void;
  /**
   * Reset language to user
   * preference
   */
  resetLanguage: () => void;
  initialLanguage: SupportedLocalesCode;
}

const ActiveLanguageContext = createContext<ContextValue | undefined>(undefined);

let savedActiveLanguage: SupportedLocalesCode = 'en';

/**
 * Returns language in a non-reactive
 * way (can be used to prevent unwanted
 * re-renders)
 */
export function getActiveLanguage() {
  return savedActiveLanguage;
}

/**
 * `browserLocale` needs to be passed
 * to initialize the provider
 */
export const ActiveLanguageProvider: React.FC<{
  browserLocale: SupportedLocalesCode;
  hotelId?: string;
}> = ({ browserLocale, children, hotelId }) => {
  const { defaultLocale, supportedLocales } = useGetAvailableLanguages({ hotelId });

  const initialLocale = useMemo(() => {
    // If browser locale is supported, use that; otherwise defaultLocale
    // Remove dash from possible `lang-Region` language string (Safari only ofc)
    const sanitizedBrowserLocale = browserLocale.split('-')[0];
    const foundIntialLocale: SupportedLocalesCode =
      supportedLocales.find((locale) => locale === sanitizedBrowserLocale) ?? defaultLocale;
    savedActiveLanguage = foundIntialLocale;
    return foundIntialLocale;
  }, [browserLocale, supportedLocales, defaultLocale]);

  const [currentContextValue, setContextValue] = useState<ContextValue>({
    activeLanguage: initialLocale,
    switchLanguage: (newLocale) => {
      savedActiveLanguage = newLocale;
      setContextValue((value) => ({ ...value, activeLanguage: newLocale }));
    },
    resetLanguage: () => {
      currentContextValue.switchLanguage(initialLocale);
    },
    initialLanguage: initialLocale,
  });

  useEffect(() => {
    //Ensures the preset activeLanguage is supported; otherwise, switch to the recomputed initialLocale
    if (!supportedLocales.includes(currentContextValue.activeLanguage)) {
      currentContextValue.switchLanguage(initialLocale);
    }
  }, [currentContextValue, initialLocale, supportedLocales]);

  return (
    <ActiveLanguageContext.Provider value={currentContextValue}>
      {children}
    </ActiveLanguageContext.Provider>
  );
};

export function useActiveLanguage() {
  const contextValue = useContext(ActiveLanguageContext);
  if (!contextValue) {
    throw new Error('useActiveLanguage must be used within an ActiveLanguageProvider');
  }
  return contextValue;
}
